/* eslint-disable import/no-default-export, import/order, max-lines, max-lines-per-function, no-nested-ternary, react/no-unescaped-entities, react/prop-types */
import { useRef, useState, useEffect } from "react";
import api from "common/utils/api";
import { notifyError } from "common/utils/app-monitoring/notify";
import { objNamesToCamelCase } from "common/utils/helpers/cases";
import UseSvg from "common/vue/components/UseSvg.react.vue";
import CheckBox from "components/jiffy/react-check-box/CheckBox.vue";
import { useDtfStore } from "../store";
import { useEscapeHatch } from "./composables/useEscapeHatch";
import { usePinningFeedback } from "./composables/usePinningFeedback";
import { usePinningPanel } from "./composables/usePinningPanel";
import {
  bulkDeletePins,
  cancelSession,
  completeSession,
  fetchPins,
  retryPreview
} from "./composables/usePinningSession";
import { usePinningToast } from "./composables/usePinningToast";
import PinningActionBar from "./PinningActionBar.vue";
import PinningArtboard from "./PinningArtboard.vue";
import PinningFeedback from "./PinningFeedback.vue";
import PinningHeader from "./PinningHeader.vue";
import PinningPanel from "./PinningPanel.vue";
import PinningToast from "./PinningToast.vue";
import PinningZoom from "./PinningZoom.vue";
import {
  trackPinSessionsGenConfirmed,
  trackPinSessionsGenDiscarded,
  trackPinsSessionCancellationAborted,
  trackPinsSessionCancelled,
  trackPinsSessionEditCancelFailed,
  trackPinsSessionEditCancelModalConfirmed,
  trackPinsSessionEditCancelModalStayed,
  trackTransfersPinningModeEntered,
  trackTransfersPinningSessionCompleted,
  trackTransfersPinningSessionCompletionFailed
} from "./transfersPinningAnalytics";
import "./PinningSessionModal.scss";

/* Vue's provide("pinningPanel") / provide("pendingInitialMessage") are replaced
by explicit props to PinningPanel — no React Context needed. */

export default function PinningSessionModal({
  artworkIndex,
  artworkImageUrl,
  artworkId,
  sessionId,
  ...restProps
}: any) {
  const PINNING_TOOLTIP_DISMISSED_KEY = "transfers-pinning-tooltip-dismissed";

  const store = useDtfStore();
  const modalRef = useRef(null);

  const [isCompleting, setIsCompleting] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [cancelModalType, setCancelModalType] = useState(null);
  const [tooltipDismissed, setTooltipDismissed] = useState(
    localStorage.getItem(PINNING_TOOLTIP_DISMISSED_KEY) === "true"
  );
  // @ts-expect-error TS(4111): Property 'pendingInitialMessage' comes from an ind... Remove this comment to see the full error message
  const [pendingInitialMessage, setPendingInitialMessage] = useState(store.pendingInitialMessage);
  const [tooltipMode, setTooltipMode] = useState(
    tooltipDismissed || pendingInitialMessage ? null : "initial"
  );
  const [zoom, setZoom] = useState(1);
  const [isLoadingPins, setIsLoadingPins] = useState(false);
  const [editInstructionText, setEditInstructionText] = useState("");

  const toast = usePinningToast();
  const panel = usePinningPanel(sessionId, String(artworkId), {
    toast,
    pendingInitialMessage
  });
  const feedback = usePinningFeedback(sessionId, panel.pins, panel.lastEditedPinId, {
    toast
  });
  const escapeHatch = useEscapeHatch(panel, {
    async onConfirm() {
      setIsCancelling(true);
      try {
        await cancelSession(sessionId);
      } catch {
        // Proceed even if the API call fails — the escape hatch must remain reliable.
      } finally {
        clearPinningSessionFromStore();
        // @ts-expect-error TS(2571): Object is of type 'unknown'.
        store.setPinBasedRedrawEscaped(true);
        setIsCancelling(false);
        hideBootstrapModal();
      }
    }
  });

  function shouldHydrateExistingSession() {
    // @ts-expect-error TS(2571): Object is of type 'unknown'.
    const artwork = store.artworks[artworkIndex];
    return artwork?.pinningSessionId === sessionId;
  }

  function newPins() {
    // @ts-expect-error TS(2339): Property 'isNew' does not exist on type 'never'.
    return panel.pins.filter(p => p.isNew);
  }

  function newPinIds() {
    // @ts-expect-error TS(2339): Property 'id' does not exist on type 'never'.
    return newPins().map(p => p.id);
  }

  function hasNewPins() {
    return newPins().length > 0;
  }

  function isNewSessionCancel() {
    return cancelModalType === "newSession";
  }

  function isUserSignedIn() {
    // @ts-expect-error TS(4111): Property 'userType' comes from an index signature,... Remove this comment to see the full error message
    return store.userType === "signed_in";
  }

  function editingPinNumber() {
    if (!panel.editingPin()) return 0;
    // @ts-expect-error TS(2339): Property 'id' does not exist on type 'never'.
    return panel.pins.findIndex(p => p.id === panel.editingPin().id) + 1;
  }

  function showTooltip() {
    return tooltipMode !== null && !isLoadingPins;
  }

  function tooltipMessage() {
    return tooltipMode === "initial"
      ? "Click anywhere on the image to drop a pin and add instructions for our artists."
      : "Click anywhere on the image to add another pin";
  }

  useEffect(() => {
    if (tooltipDismissed || pendingInitialMessage) {
      if (tooltipMode !== null) setTooltipMode(null);
      return;
    }
    if (panel.pins.length > 0 && tooltipMode === "initial") {
      setTooltipMode(null);
      return;
    }
    if (panel.hasUnfinishedPin()) {
      setTooltipMode(null);
      return;
    }
    if (!panel.hasCompletedPin() || !panel.canAddNewPin()) {
      if (tooltipMode === "add-next") {
        setTooltipMode(null);
      }
      return;
    }
    setTooltipMode("add-next");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [panel.pins.length, panel.hasCompletedPin(), panel.canAddNewPin(), panel.hasUnfinishedPin()]);

  useEffect(() => {
    if (panel.editingPin()) {
      // @ts-expect-error TS(2531): Object is possibly 'null'.
      setEditInstructionText(panel.editingPin().instruction || "");
    }
  }, [panel.editingPin()]);

  const PINNING_MODAL_Z_INDEX = 2147483000;
  const PINNING_MODAL_BACKDROP_Z_INDEX = 2147482990;

  function applyModalStackingOrder() {
    if (modalRef.current) {
      // @ts-expect-error TS(2339): Property 'style' does not exist on type 'never'.
      modalRef.current.style.zIndex = String(PINNING_MODAL_Z_INDEX);
    }
    const latestBackdrop = document.querySelector(".modal-backdrop:last-of-type");
    if (latestBackdrop instanceof HTMLElement) {
      latestBackdrop.style.zIndex = String(PINNING_MODAL_BACKDROP_Z_INDEX);
    }
  }

  function showBootstrapModal() {
    // @ts-expect-error TS(2581): Cannot find name '$'. Do you need to install type ... Remove this comment to see the full error message
    const $modal = $(modalRef.current);
    $modal
      .modal({
        backdrop: "static",
        keyboard: false
      })
      .modal("show")
      .on("shown.bs.modal", () => {
        applyModalStackingOrder();
      })
      .on("hidden.bs.modal", () => {
        $modal.off("shown.bs.modal");
        $modal.off("hidden.bs.modal");
        // @ts-expect-error TS(2571): Object is of type 'unknown'.
        store.closePinningSession();
      });
  }

  function hideBootstrapModal() {
    // @ts-expect-error TS(2581): Cannot find name '$'. Do you need to install type ... Remove this comment to see the full error message
    $(modalRef.current).modal("hide");
  }

  async function attemptPreviewRetry(readyPin: any) {
    try {
      const result = await retryPreview(sessionId);
      if (result.previewReady && result.generatedImageUrl) {
        const pinId = readyPin.pinId || readyPin.id || "";
        panel.setIsPreviewImageLoaded(false);
        panel.setPreviewApproval({
          // @ts-expect-error TS migration
          pinId,
          generatedImageUrl: result.generatedImageUrl,
          instruction: readyPin.instruction || null
        });
        panel.setActivePinId(pinId);
      }
    } catch {
      // Pin stays in ready state — escalation card will render
    }
  }

  async function hydrateExistingSession() {
    if (!shouldHydrateExistingSession()) return;
    setIsLoadingPins(true);
    try {
      const { pins: pinsData, generatedImageUrl } = await fetchPins(sessionId);
      panel.hydratePins(pinsData);
      setTooltipMode(null);

      if (generatedImageUrl) {
        const readyPin = pinsData.find((p: any) => p.state === "ready" || p.status === "ready");
        // @ts-expect-error TS migration
        const pinId = readyPin?.pinId || readyPin?.id || "";
        panel.setIsPreviewImageLoaded(false);
        panel.setPreviewApproval({
          // @ts-expect-error TS migration
          pinId,
          generatedImageUrl,
          instruction: readyPin?.instruction || null
        });
        panel.setActivePinId(pinId);
      } else {
        const readyPin = pinsData.find((p: any) => p.state === "ready" || p.status === "ready");
        // @ts-expect-error TS(2571): Object is of type 'unknown'.
        const artwork = store.artworks[artworkIndex];
        const sessionState = artwork?.pinningSessionState;
        if (readyPin && (sessionState === "active" || sessionState === "generating_preview")) {
          await attemptPreviewRetry(readyPin);
        }
      }
    } catch {
      toast.error({
        title: "Failed to load pins",
        description: "Something went wrong. Please try again."
      });
    } finally {
      setIsLoadingPins(false);
    }
  }

  useEffect(() => {
    (async () => {
      showBootstrapModal();
      await hydrateExistingSession();
      trackTransfersPinningModeEntered({
        artworkId,
        sessionId,
        hasExistingPins: shouldHydrateExistingSession(),
        initialMessage: pendingInitialMessage
      });
    })();

    return () => {
      // @ts-expect-error TS(4111): Property 'pendingInitialMessage' comes from an ind... Remove this comment to see the full error message
      store.pendingInitialMessage = null;
      feedback.resetFeedback();
      toast.clearAll();
      // @ts-expect-error TS(2581): Cannot find name '$'. Do you need to install type ... Remove this comment to see the full error message
      $(modalRef.current).modal("hide");
    };
  }, []);

  function dismissTooltip() {
    if (tooltipMode === "add-next") {
      setTooltipDismissed(true);
      localStorage.setItem(PINNING_TOOLTIP_DISMISSED_KEY, "true");
    }
    setTooltipMode(null);
  }

  function clearPinningSessionFromStore() {
    // @ts-expect-error TS(2571): Object is of type 'unknown'.
    store.updateArtworkValuesById({
      id: artworkId,
      pinningSessionId: null,
      pinningSummary: null,
      pinningSessionState: null
    });
  }

  function handleStayOnCancelModal() {
    if (cancelModalType === "editSession") {
      trackPinsSessionEditCancelModalStayed({
        sessionId,
        newPinsCount: newPinIds().length
      });
    } else if (cancelModalType === "newSession") {
      trackPinsSessionCancellationAborted({
        artworkId,
        sessionId,
        pinsCount: panel.pins.length
      });
    }
    setCancelModalType(null);
  }

  function handleAddPin(coordinates: any) {
    if (panel.canRepositionActivePin()) {
      panel.repositionActivePin(coordinates);
      return;
    }
    if (tooltipMode === "add-next" && !tooltipDismissed) {
      setTooltipDismissed(true);
      localStorage.setItem(PINNING_TOOLTIP_DISMISSED_KEY, "true");
    }
    panel.addPin(coordinates);
  }

  function handlePinClick(pinId: any) {
    const currentActive = panel.activePin();
    // @ts-expect-error TS(2339): Property 'state' does not exist on type 'never'.
    if (currentActive && currentActive.state !== "ready" && currentActive.state !== "unsupported") {
      return;
    }
    if (pinId !== panel.activePinId) {
      // @ts-expect-error TS(2339): Property 'id' does not exist on type 'never'.
      const pin = panel.pins.find(p => p.id === pinId);
      if (pin) {
        // @ts-expect-error TS(2339): Property 'state' does not exist on type 'never'.
        panel.trackPinSelected(pinId, panel.activePinId, pin.state);
      }
    }
    panel.setActivePinId(pinId === panel.activePinId ? null : pinId);
  }

  function handleCancel() {
    if (!artworkId || !sessionId) {
      hideBootstrapModal();
      return;
    }
    if (panel.hasExistingPin() && hasNewPins()) {
      // @ts-expect-error TS(2345): Argument of type '"editSession"' is not assignable... Remove this comment to see the full error message
      setCancelModalType("editSession");
      return;
    }
    if (panel.hasExistingPin() && !hasNewPins()) {
      trackPinsSessionCancelled({
        artworkId,
        sessionId,
        pinsCount: panel.pins.length,
        readyPinsCount: panel.readyPinsCount()
      });
      hideBootstrapModal();
      return;
    }
    if (panel.pins.length > 0) {
      // @ts-expect-error TS(2345): Argument of type '"newSession"' is not assignable ... Remove this comment to see the full error message
      setCancelModalType("newSession");
      return;
    }
    setIsCancelling(true);
    cancelSession(sessionId)
      .then(() => {
        trackPinsSessionCancelled({
          artworkId,
          sessionId,
          pinsCount: panel.pins.length,
          readyPinsCount: panel.readyPinsCount()
        });
        hideBootstrapModal();
      })
      .catch(() => {
        toast.error({
          title: "Failed to cancel session",
          description: "Something went wrong. Please try again."
        });
        hideBootstrapModal();
      })
      .finally(() => {
        clearPinningSessionFromStore();
        setIsCancelling(false);
      });
  }

  async function confirmCancel() {
    if (cancelModalType === "editSession") {
      setIsCancelling(true);
      const unsyncedPinIds = newPins()
        // @ts-expect-error TS(2339): Property 'state' does not exist on type 'never'.
        .filter(p => p.state === "draft" && p.messages.length === 0)
        // @ts-expect-error TS(2339): Property 'id' does not exist on type 'never'.
        .map(p => p.id);
      const syncedPinIds = newPinIds().filter(id => !unsyncedPinIds.includes(id));

      if (unsyncedPinIds.length > 0) {
        // @ts-expect-error TS(2339): Property 'id' does not exist on type 'never'.
        panel.setPins(panel.pins.filter(p => !unsyncedPinIds.includes(p.id)));
      }

      if (syncedPinIds.length === 0) {
        setCancelModalType(null);
        setIsCancelling(false);
        hideBootstrapModal();
        return;
      }

      trackPinsSessionEditCancelModalConfirmed({
        sessionId,
        newPinsCount: syncedPinIds.length
      });

      try {
        await bulkDeletePins(sessionId, syncedPinIds);
        setCancelModalType(null);
        hideBootstrapModal();
      } catch (err) {
        trackPinsSessionEditCancelFailed({
          sessionId,
          newPinsCount: syncedPinIds.length,
          // @ts-expect-error TS(2571): Object is of type 'unknown'.
          errorMessage: err?.message || "Unknown error"
        });
        toast.error({
          title: "Failed to remove new pins",
          description: "Something went wrong. Please try again."
        });
      } finally {
        setIsCancelling(false);
      }
      return;
    }

    setIsCancelling(true);
    try {
      const submittedPinIds = panel.pins
        // @ts-expect-error TS(2339): Property 'state' does not exist on type 'never'.
        .filter(p => p.state === "ready" || p.state === "unsupported")
        // @ts-expect-error TS(2339): Property 'id' does not exist on type 'never'.
        .map(p => p.id);
      if (submittedPinIds.length > 0) {
        await bulkDeletePins(sessionId, submittedPinIds);
      }
      await cancelSession(sessionId);
      trackPinsSessionCancelled({
        artworkId,
        sessionId,
        pinsCount: panel.pins.length,
        readyPinsCount: panel.readyPinsCount()
      });
    } catch {
      // Close even if cleanup fails
    } finally {
      clearPinningSessionFromStore();
      setIsCancelling(false);
      setCancelModalType(null);
      hideBootstrapModal();
    }
  }

  async function handleEscalationConfirm() {
    if (panel.hasAiAssistanceOngoing()) return;
    setIsCompleting(true);
    try {
      const data = await completeSession(sessionId);
      // @ts-expect-error TS(2571): Object is of type 'unknown'.
      store.completePinningSession({
        artworkIndex,
        sessionId,
        summary: data.summary ?? null,
        state: data.state ?? "completed"
      });
      try {
        trackTransfersPinningSessionCompleted({
          artworkId,
          sessionId,
          pinsCount: panel.pins.length,
          readyPinsCount: panel.readyPinsCount(),
          summary: data.summary ?? ""
        });
      } catch (e) {
        // @ts-expect-error TS migration
        notifyError(e, { context: { sessionId } });
      }
      hideBootstrapModal();
    } catch (err) {
      trackTransfersPinningSessionCompletionFailed({
        artworkId,
        sessionId,
        // @ts-expect-error TS(2571): Object is of type 'unknown'.
        errorMessage: err?.message || "Unknown error"
      });
      toast.error({
        title: "Failed to complete session",
        description: "Something went wrong. Please try again."
      });
    } finally {
      setIsCompleting(false);
    }
  }

  function handleEscalationCancel(pinId: any) {
    panel.handleDeletePin(pinId);
  }

  async function savePreviewAsManualEdit(imageUrl: string) {
    const imageResponse = await fetch(imageUrl);
    if (!imageResponse.ok) throw new Error(`Image fetch failed: ${imageResponse.status}`);
    const blob = await imageResponse.blob();
    const formData = new FormData();
    formData.append("image", blob);
    formData.append("manual_edit", "true");
    formData.append("remove_background", "false");
    formData.append("start_new_edit", "false");
    formData.append("ai_edited", "true");
    const refineResult = objNamesToCamelCase(
      await api.dtf.completeRefineSession({ dtf_artwork_id: artworkId }, formData, {
        raw: true,
        useAxiosForRequest: true,
        headers: { "Content-Type": undefined }
      })
    );
    // @ts-expect-error TS(2571): Object is of type 'unknown'.
    store.updateArtworkValuesById({
      id: artworkId,
      manualEditUrl: refineResult.manualEditUrl || null,
      manualEditNoTrimUrl: refineResult.manualEditNoTrimUrl || null,
      editImageManual: true
    });
  }

  async function handleApprovePreview() {
    const data = await panel.handleApprovePreview();
    if (!data) return;

    try {
      // @ts-expect-error TS(2339): Property 'generatedImageUrl' does not exist on type 'never'.
      await savePreviewAsManualEdit(panel.previewApproval?.generatedImageUrl);
    } catch {
      panel.setIsApproving(false);
      toast.error({
        title: "Failed to save image",
        description: "Something went wrong. Please try again."
      });
      return;
    }

    // @ts-expect-error TS(2571): Object is of type 'unknown'.
    store.completePinningSession({
      artworkIndex,
      sessionId,
      summary: data.summary ?? null,
      state: data.state ?? "approved"
    });
    trackPinSessionsGenConfirmed({
      artworkId,
      sessionId,
      // @ts-expect-error TS(2339): Property 'pinId' does not exist on type 'never'.
      pinId: panel.previewApproval?.pinId
    });
    hideBootstrapModal();
  }

  async function handleRejectPreview() {
    const data = await panel.handleRejectPreview();
    if (!data) return;
    // @ts-expect-error TS(2571): Object is of type 'unknown'.
    store.completePinningSession({
      artworkIndex,
      sessionId,
      summary: data.summary ?? null,
      state: data.state ?? "completed"
    });
    trackPinSessionsGenDiscarded({
      artworkId,
      sessionId,
      // @ts-expect-error TS(2339): Property 'pinId' does not exist on type 'never'.
      pinId: panel.previewApproval?.pinId
    });
    hideBootstrapModal();
  }

  return (
    <div
      {...restProps}
      className="modal pinning-session-modal scope-79eb8679"
      ref={modalRef}
      role="dialog"
      data-testid="pinning-session-modal"
    >
      <div className="pinning-session-modal__dialog modal-dialog scope-79eb8679">
        {}
        {isLoadingPins ? (
          <div
            className="scope-79eb8679 absolute inset-0 z-50 flex items-center justify-center bg-white/70"
            data-testid="artboard-loading-overlay"
          >
            <div className="scope-79eb8679 flex flex-col items-center gap-2">
              <UseSvg
                className="scope-79eb8679 h-8 w-8 animate-spin text-blue-700"
                icon="loader"
                data-testid="h-8"
              />
              <span className="scope-79eb8679 text-sm font-medium text-gray-700">
                Loading pins...
              </span>
            </div>
          </div>
        ) : null}
        {!panel.isDesktop ? (
          <>
            <PinningHeader
              className="scope-79eb8679"
              isDesktop={false}
              data-testid="pinning-header"
            />
            <div className="scope-79eb8679 flex min-h-0 flex-1 flex-col-reverse">
              {}
              <div
                className={`scope-79eb8679 mx-2 mb-1 shrink-0 overflow-hidden rounded-xl bg-white shadow-[0_4px_12px_rgba(0,0,0,0.1)] transition-all duration-300 ease-out ${
                  panel.isPanelExpanded ? "mt-1 max-h-screen min-h-0 flex-1" : "max-h-[40vh]"
                }`}
              >
                <PinningPanel
                  className="scope-79eb8679"
                  isDesktop={false}
                  isCompletingSession={isCompleting}
                  panel={panel}
                  pendingInitialMessage={pendingInitialMessage}
                  onApprovePreview={handleApprovePreview}
                  onRejectPreview={handleRejectPreview}
                  onEscalationConfirm={handleEscalationConfirm}
                  onEscalationCancel={handleEscalationCancel}
                  data-testid="pinning-panel"
                />
              </div>
              {}
              <div
                className={`scope-79eb8679 relative flex-1 overflow-hidden transition-all duration-300 ease-out ${
                  panel.isPanelExpanded ? "max-h-0" : "max-h-screen"
                }`}
              >
                <PinningArtboard
                  className="scope-79eb8679"
                  imageUrl={artworkImageUrl}
                  // @ts-expect-error TS(2339): Property 'generatedImageUrl' does not exist on typ... Remove this comment to see the full error message
                  generatedImageUrl={panel.previewApproval?.generatedImageUrl || ""}
                  pins={panel.pins}
                  activePinId={panel.activePinId}
                  hoveredPinId={panel.hoveredPinId}
                  showPins={panel.showPins}
                  isShowingEditPreview={!!panel.previewApproval}
                  canAddPin={
                    !panel.previewApproval &&
                    (panel.canAddNewPin() || panel.canRepositionActivePin())
                  }
                  zoom={zoom}
                  showTooltip={!panel.previewApproval && showTooltip()}
                  tooltipMessage={tooltipMessage()}
                  sessionId={sessionId}
                  artworkId={String(artworkId)}
                  onAddPin={handleAddPin}
                  onPinClick={handlePinClick}
                  onPinHover={(id: any) => {
                    panel.setHoveredPinId(id);
                    // @ts-expect-error TS(2345): Argument of type '"artboard"' is not assignable to... Remove this comment to see the full error message
                    panel.setHoveredPinSource("artboard");
                  }}
                  onPinUnhover={() => {
                    panel.setHoveredPinId(null);
                    panel.setHoveredPinSource(null);
                  }}
                  onTooltipDismiss={dismissTooltip}
                  onImageLoaded={() => {
                    panel.setIsPreviewImageLoaded(true);
                  }}
                  data-testid="pinning-artboard"
                />
              </div>
            </div>
            {}
            <div className="scope-79eb8679 flex shrink-0 items-center justify-center gap-3 py-1">
              <div className="scope-79eb8679 flex h-10 items-center rounded-lg bg-white px-3 shadow-md">
                <CheckBox
                  className="scope-79eb8679"
                  label="Show pins"
                  style={"gray"}
                  data-testid="show-pins-toggle"
                >
                  <input
                    className="scope-79eb8679"
                    type="checkbox"
                    checked={panel.showPins}
                    onChange={e => {
                      panel.setShowPins(e.target.checked);
                    }}
                    data-testid="pinning-session-modal-input"
                  />
                </CheckBox>
              </div>
              <PinningZoom
                className="scope-79eb8679"
                zoom={zoom}
                isDesktop={false}
                onUpdateZoom={(val: any) => setZoom(val)}
                data-testid="pinning-zoom"
              />
            </div>
            {isUserSignedIn() ? (
              <PinningFeedback
                className="scope-79eb8679"
                positive={feedback.positive}
                message={feedback.message}
                isOpen={feedback.isOpen}
                status={feedback.status}
                isDesktop={false}
                onSelectPositive={feedback.handleSelectPositive}
                onSelectNegative={feedback.handleSelectNegative}
                onCancel={feedback.handleCancel}
                onSubmit={feedback.handleSubmit}
                onUpdateMessage={(val: any) => {
                  feedback.setMessage(val);
                }}
                data-testid="pinning-feedback"
              />
            ) : null}
            <PinningActionBar
              className="scope-79eb8679"
              variant="static"
              isCancelDisabled={isCompleting || isCancelling || panel.isApproving}
              onCancel={handleCancel}
              onEscapeHatchClick={escapeHatch.handleEscapeHatchClick}
              data-testid="pinning-action-bar"
            />
          </>
        ) : (
          <div className="scope-79eb8679 flex h-full flex-1 gap-3 overflow-hidden">
            <div className="scope-79eb8679 flex w-2/5 min-w-[400px] max-w-[700px] flex-col overflow-hidden border border-gray-200 bg-white shadow">
              <PinningHeader
                className="scope-79eb8679"
                isDesktop={true}
                data-testid="pinning-header-desktop"
              />
              <PinningPanel
                className="scope-79eb8679"
                isDesktop={true}
                isCompletingSession={isCompleting}
                panel={panel}
                pendingInitialMessage={pendingInitialMessage}
                onApprovePreview={handleApprovePreview}
                onRejectPreview={handleRejectPreview}
                onEscalationConfirm={handleEscalationConfirm}
                onEscalationCancel={handleEscalationCancel}
                data-testid="pinning-panel-1"
              />
            </div>
            <div className="scope-79eb8679 relative flex-1 overflow-hidden">
              <PinningArtboard
                className="scope-79eb8679"
                isDesktop={true}
                isFeedbackVisible={isUserSignedIn()}
                imageUrl={artworkImageUrl}
                // @ts-expect-error TS(2339): Property 'generatedImageUrl' does not exist on typ... Remove this comment to see the full error message
                generatedImageUrl={panel.previewApproval?.generatedImageUrl || ""}
                pins={panel.pins}
                activePinId={panel.activePinId}
                hoveredPinId={panel.hoveredPinId}
                showPins={panel.showPins}
                isShowingEditPreview={!!panel.previewApproval}
                canAddPin={
                  !panel.previewApproval && (panel.canAddNewPin() || panel.canRepositionActivePin())
                }
                zoom={zoom}
                showTooltip={!panel.previewApproval && showTooltip()}
                tooltipMessage={tooltipMessage()}
                sessionId={sessionId}
                artworkId={String(artworkId)}
                onAddPin={handleAddPin}
                onPinClick={handlePinClick}
                onPinHover={(id: any) => {
                  panel.setHoveredPinId(id);
                  // @ts-expect-error TS(2345): Argument of type '"artboard"' is not assignable to... Remove this comment to see the full error message
                  panel.setHoveredPinSource("artboard");
                }}
                onPinUnhover={() => {
                  panel.setHoveredPinId(null);
                  panel.setHoveredPinSource(null);
                }}
                onTooltipDismiss={dismissTooltip}
                onImageLoaded={() => {
                  panel.setIsPreviewImageLoaded(true);
                }}
                data-testid="pinning-artboard-1"
              />
              <div className="scope-79eb8679 absolute right-2 top-2 z-10 flex items-center gap-2">
                {!panel.previewApproval ? (
                  <div className="scope-79eb8679 flex h-10 items-center rounded-lg bg-white px-3 shadow-md">
                    <CheckBox
                      className="scope-79eb8679"
                      label="Show pins"
                      style={"gray"}
                      data-testid="show-pins-toggle"
                    >
                      <input
                        className="scope-79eb8679"
                        type="checkbox"
                        checked={panel.showPins}
                        onChange={e => {
                          panel.setShowPins(e.target.checked);
                        }}
                        data-testid="pinning-session-modal-input-1"
                      />
                    </CheckBox>
                  </div>
                ) : null}
                <PinningZoom
                  className="scope-79eb8679"
                  zoom={zoom}
                  isDesktop={true}
                  onUpdateZoom={(val: any) => setZoom(val)}
                  data-testid="pinning-zoom-1"
                />
              </div>
              {isUserSignedIn() ? (
                <PinningFeedback
                  className="scope-79eb8679"
                  positive={feedback.positive}
                  message={feedback.message}
                  isOpen={feedback.isOpen}
                  status={feedback.status}
                  isDesktop={true}
                  onSelectPositive={feedback.handleSelectPositive}
                  onSelectNegative={feedback.handleSelectNegative}
                  onCancel={feedback.handleCancel}
                  onSubmit={feedback.handleSubmit}
                  onUpdateMessage={(val: any) => {
                    feedback.setMessage(val);
                  }}
                  data-testid="pinning-feedback-1"
                />
              ) : null}
              <PinningActionBar
                className="scope-79eb8679"
                isCancelDisabled={isCompleting || isCancelling || panel.isApproving}
                onCancel={handleCancel}
                onEscapeHatchClick={escapeHatch.handleEscapeHatchClick}
                data-testid="pinning-action-bar-1"
              />
            </div>
          </div>
        )}
      </div>
      {}
      {}
      {cancelModalType !== null ? (
        <div
          className="scope-79eb8679 fixed inset-0 z-[10001] flex items-center justify-center bg-black/50"
          onClick={() => !isCancelling && handleStayOnCancelModal()}
          data-testid="fixed"
        >
          <div
            className="scope-79eb8679 w-full max-w-sm rounded-lg bg-white p-0 shadow-xl"
            onClick={e => {
              e.stopPropagation();
            }}
            data-testid="w-full"
          >
            <div
              className={`scope-79eb8679 px-6 py-4 ${
                isNewSessionCancel()
                  ? "flex items-center justify-between"
                  : "border-b border-gray-200"
              }`}
            >
              <h3 className="scope-79eb8679 m-0 text-base font-semibold text-gray-700">
                {isNewSessionCancel() ? "Leave without saving?" : "Remove new pins?"}
              </h3>
              {isNewSessionCancel() ? (
                <button
                  type="button"
                  className="scope-79eb8679 cursor-pointer border-0 bg-transparent p-0 text-gray-400 hover:text-gray-600 disabled:cursor-not-allowed disabled:opacity-25"
                  data-testid="pinning-cancel-modal-close"
                  aria-label="Close"
                  disabled={isCancelling}
                  onClick={handleStayOnCancelModal}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              ) : null}
            </div>
            <div
              className={`scope-79eb8679 ${
                isNewSessionCancel() ? "px-6 pb-6 pt-2" : "px-6 pb-6 pt-4 text-center"
              }`}
            >
              <p
                className={`scope-79eb8679 m-0 mb-4 text-base text-gray-700 ${
                  isNewSessionCancel() ? "" : "font-medium"
                }`}
              >
                {isNewSessionCancel()
                  ? "Your edit request will be lost."
                  : `This will remove ${newPinIds().length} pin${
                      newPinIds().length === 1 ? "" : "s"
                    }, are you sure?`}
              </p>
              <div
                className={`scope-79eb8679 ${
                  isNewSessionCancel() ? "grid grid-cols-2 gap-3" : "flex flex-col gap-2"
                }`}
              >
                <button
                  className={`scope-79eb8679 w-full cursor-pointer rounded-[8px] border [font-family:inherit] disabled:pointer-events-none disabled:opacity-25 ${
                    isNewSessionCancel()
                      ? "border-red-500 bg-white px-3.5 py-2.5 text-base font-semibold text-red-500"
                      : "border-[#414192] bg-[#414192] px-3.5 py-2 text-base font-semibold !text-white"
                  }`}
                  type="button"
                  disabled={isCancelling}
                  data-testid="pinning-confirm-cancel"
                  onClick={confirmCancel}
                >
                  {isCancelling ? (
                    <>{isNewSessionCancel() ? "Exiting..." : "Removing pins..."}</>
                  ) : (
                    <>
                      {isNewSessionCancel()
                        ? "Yes, exit"
                        : `Remove pin${newPinIds().length === 1 ? "" : "s"}?`}
                    </>
                  )}
                </button>
                <button
                  className={`scope-79eb8679 w-full cursor-pointer rounded-[8px] border [font-family:inherit] disabled:pointer-events-none disabled:opacity-25 ${
                    isNewSessionCancel()
                      ? "border-[#414192] bg-[#414192] px-3.5 py-2.5 text-base font-semibold !text-white"
                      : "border-[#414192] bg-white px-3.5 py-2 text-base font-semibold"
                  }`}
                  type="button"
                  style={isNewSessionCancel() ? undefined : { color: "#414192" }}
                  disabled={isCancelling}
                  onClick={handleStayOnCancelModal}
                  data-testid="w-full-1"
                >
                  {isNewSessionCancel() ? "Keep editing" : "Stay on page"}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
      {panel.pendingDeletePinId ? (
        <div
          className="scope-79eb8679 fixed inset-0 z-[10001] flex items-center justify-center bg-black/50"
          onClick={panel.closeDeletePinModal}
          data-testid="fixed-1"
        >
          <div
            className="scope-79eb8679 w-full max-w-sm rounded-lg bg-white p-0 shadow-xl"
            onClick={e => {
              e.stopPropagation();
            }}
            data-testid="w-full-2"
          >
            <div className="scope-79eb8679 border-b border-gray-200 px-6 py-4">
              <h3 className="scope-79eb8679 m-0 text-base font-semibold text-gray-700">
                Delete pin?
              </h3>
            </div>
            <div className="scope-79eb8679 px-6 pb-6 pt-4 text-center">
              <p className="scope-79eb8679 m-0 mb-4 text-base font-medium text-gray-700">
                {" "}
                Are you sure you want to delete the pin?{" "}
              </p>
              <div className="scope-79eb8679 flex flex-col gap-2">
                <button
                  className="scope-79eb8679 w-full cursor-pointer rounded-[8px] border border-[#c90505] bg-[#c90505] px-3.5 py-2 text-base font-semibold !text-white [font-family:inherit] disabled:pointer-events-none disabled:opacity-25"
                  type="button"
                  onClick={panel.confirmDeletePin}
                  data-testid="w-full-3"
                >
                  {" "}
                  Delete{" "}
                </button>
                <button
                  className="scope-79eb8679 w-full cursor-pointer rounded-[8px] border border-[#414192] bg-white px-3.5 py-2 text-base font-semibold [font-family:inherit] disabled:pointer-events-none disabled:opacity-25"
                  type="button"
                  style={{ color: "#414192" }}
                  onClick={panel.closeDeletePinModal}
                  data-testid="w-full-4"
                >
                  {" "}
                  Cancel{" "}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
      {panel.editingPinId ? (
        <div
          className="scope-79eb8679 fixed inset-0 z-[10001] flex items-center justify-center bg-black/50"
          onClick={panel.closeEditPinModal}
          data-testid="fixed-2"
        >
          <div
            className="scope-79eb8679 w-full max-w-sm rounded-lg bg-white p-0 shadow-xl"
            onClick={e => {
              e.stopPropagation();
            }}
            data-testid="w-full-5"
          >
            <div className="scope-79eb8679 border-b border-gray-200 px-6 py-4">
              <h3 className="scope-79eb8679 m-0 text-base font-semibold text-gray-700">
                {" "}
                Edit Pin {editingPinNumber()} Instructions{" "}
              </h3>
            </div>
            <div className="scope-79eb8679 px-6 pb-6 pt-4 text-center">
              <textarea
                className="mp-unmask font-inherit scope-79eb8679 mb-4 w-full resize-none rounded-lg border border-gray-300 p-2.5 outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600"
                value={editInstructionText}
                onChange={e => {
                  setEditInstructionText(e.target.value);
                }}
                style={{ fontSize: "16px" }}
                // @ts-expect-error TS(2322): Type 'string' is not assignable to type 'number'.
                rows="4"
                data-testid="edit-pin-instruction-textarea"
              />
              <div className="scope-79eb8679 flex flex-col gap-2">
                <button
                  className="scope-79eb8679 w-full cursor-pointer rounded-[8px] border border-[#414192] bg-[#414192] px-3.5 py-2 text-base font-semibold !text-white [font-family:inherit] disabled:pointer-events-none disabled:opacity-25"
                  type="button"
                  disabled={panel.isUpdatingPinInstruction}
                  onClick={() => {
                    panel.handleUpdatePinInstruction(editInstructionText);
                  }}
                  data-testid="w-full-6"
                >
                  {panel.isUpdatingPinInstruction ? "Saving..." : "Save"}
                </button>
                <button
                  className="scope-79eb8679 w-full cursor-pointer rounded-[8px] border border-[#414192] bg-white px-3.5 py-2 text-base font-semibold [font-family:inherit] disabled:pointer-events-none disabled:opacity-25"
                  type="button"
                  style={{ color: "#414192" }}
                  onClick={panel.closeEditPinModal}
                  data-testid="w-full-7"
                >
                  {" "}
                  Cancel{" "}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
      {escapeHatch.escapeHatchModalOpen ? (
        <div
          className="scope-79eb8679 fixed inset-0 z-[10001] flex items-center justify-center bg-black/50"
          onClick={escapeHatch.handleEscapeHatchDismiss}
          data-testid="fixed-4"
        >
          <div
            className="scope-79eb8679 w-full max-w-sm rounded-lg bg-white p-0 shadow-xl"
            onClick={e => {
              e.stopPropagation();
            }}
            data-testid="w-full-10"
          >
            <div className="scope-79eb8679 border-b border-gray-200 px-6 py-4">
              <h3 className="scope-79eb8679 m-0 text-base font-semibold text-gray-700">
                {" "}
                Use classic edit form?{" "}
              </h3>
            </div>
            <div className="scope-79eb8679 px-6 pb-6 pt-4 text-center">
              <p className="scope-79eb8679 m-0 mb-4 text-base font-medium text-gray-700">
                {" "}
                This will close the pin editor and let you describe your edits in a text form. Your
                placed pins will not be saved. Continue?{" "}
              </p>
              <div className="scope-79eb8679 flex flex-col gap-2">
                <button
                  className="scope-79eb8679 w-full cursor-pointer rounded-[8px] border border-[#414192] bg-[#414192] px-3.5 py-2 text-base font-semibold !text-white [font-family:inherit] disabled:pointer-events-none disabled:opacity-25"
                  type="button"
                  disabled={isCancelling}
                  data-testid="escape-hatch-confirm"
                  onClick={escapeHatch.handleEscapeHatchConfirm}
                >
                  {isCancelling ? "Switching..." : "Continue"}
                </button>
                <button
                  className="scope-79eb8679 w-full cursor-pointer rounded-[8px] border border-[#414192] bg-white px-3.5 py-2 text-base font-semibold [font-family:inherit] disabled:pointer-events-none disabled:opacity-25"
                  type="button"
                  style={{ color: "#414192" }}
                  data-testid="escape-hatch-dismiss"
                  onClick={escapeHatch.handleEscapeHatchDismiss}
                >
                  {" "}
                  Stay here{" "}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
      <PinningToast
        className="scope-79eb8679"
        toasts={toast.toasts}
        onDismiss={toast.dismiss}
        data-testid="pinning-toast"
      />
    </div>
  );
}
